import React from 'react'
import {Avatar, ButtonWithActionModal, Tag} from '@therms/atalaya'
import {
  AvailabilityChangeRequest,
  PreferredDay,
  SchedulingUserAvailability,
  TimeOffRequest,
} from '@therms/react-modules'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {UnavailableTimesSection} from '@src/modules/Scheduling/components/UnavailableTimesSection'
import {TimeOffSection} from '@src/modules/Scheduling/components/TimeOffSection'
import {PreferredDaysSection} from '@src/modules/Scheduling/components/PreferredDaysSection'
import {SectionShell} from '@src/modules/Scheduling/components/SectionShell'

dayjs.extend(relativeTime)

// Enough to explain the latest outcome without becoming a log.
const RECENT_DECISIONS = 3

const DECISION_TAGS = {
  approved: {label: 'Approved', variant: 'positive'},
  declined: {label: 'Declined', variant: 'critical'},
  withdrawn: {label: 'Withdrawn', variant: 'neutral'},
} as const

type Decided = AvailabilityChangeRequest & {status: keyof typeof DECISION_TAGS}

const isDecided = (change: AvailabilityChangeRequest): change is Decided =>
  change.status in DECISION_TAGS

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('')
}

interface SchedulingTabViewProps {
  availability?: SchedulingUserAvailability
  /** The user's availability changes, newest first (the pending one included). */
  availabilityChanges?: AvailabilityChangeRequest[]
  canEditAvailability: boolean
  canRequestTimeOff: boolean
  currentUserId: string
  /** The primary region, when its days fall differently from the device's. */
  daysZoneLabel?: React.ReactNode
  /** The region's first day of the week; every day list starts on it. */
  firstDayOfWeek?: string
  onChangePreferredDays: (days: PreferredDay[]) => void
  onEditAvailability: () => void
  onEditTimeOff: (request: TimeOffRequest) => void
  onRequestTimeOff: () => void
  onWithdrawAvailabilityChange: (
    change: AvailabilityChangeRequest,
  ) => Promise<void>
  onWithdrawTimeOff: (request: TimeOffRequest) => Promise<void>
  timeOffRequests: TimeOffRequest[]
  user: {avatarUrl?: string; displayName: string; title?: string}
}

export function SchedulingTabView({
  availability,
  availabilityChanges = [],
  canEditAvailability,
  canRequestTimeOff,
  currentUserId,
  daysZoneLabel,
  firstDayOfWeek,
  onChangePreferredDays,
  onEditAvailability,
  onEditTimeOff,
  onRequestTimeOff,
  onWithdrawAvailabilityChange,
  onWithdrawTimeOff,
  timeOffRequests,
  user,
}: SchedulingTabViewProps) {
  const pendingRequest = availabilityChanges.find(
    (change) => change.status === 'pending',
  )
  const recentDecisions = availabilityChanges
    .filter(isDecided)
    .slice(0, RECENT_DECISIONS)

  // While a change request is pending, the sections display the requested
  // (new) values, per design 3c.
  const shown = pendingRequest ? pendingRequest.requested : availability

  const pendingNote = pendingRequest ? (
    <>
      Submitted{' '}
      {pendingRequest.history?.createdAt
        ? dayjs(pendingRequest.history.createdAt).fromNow()
        : 'recently'}{' '}
      · awaiting manager approval
    </>
  ) : undefined

  return (
    <div className="gap-lg mx-auto flex w-full max-w-[760px] flex-col">
      <div className="gap-sm flex items-center">
        <Avatar
          circular
          size="lg"
          symbol={getInitials(user.displayName)}
          url={user.avatarUrl}
        />
        <div className="min-w-0">
          <div className="text-color-strong truncate text-xl font-semibold">
            {user.displayName}
          </div>
          {user.title && (
            <div className="text-color-subtle truncate text-sm">
              {user.title}
            </div>
          )}
        </div>
      </div>

      <UnavailableTimesSection
        blocks={shown?.unavailableTimes}
        canEdit={canEditAvailability}
        currentBlocks={
          pendingRequest ? availability?.unavailableTimes : undefined
        }
        firstDayOfWeek={firstDayOfWeek}
        note={pendingNote}
        onEdit={onEditAvailability}
        pending={!!pendingRequest}
      />

      <TimeOffSection
        canRequest={canRequestTimeOff}
        currentUserId={currentUserId}
        onEdit={onEditTimeOff}
        onRequest={onRequestTimeOff}
        onWithdraw={onWithdrawTimeOff}
        requests={timeOffRequests}
      />

      <PreferredDaysSection
        canEdit={canEditAvailability}
        currentDays={pendingRequest ? availability?.preferredDays : undefined}
        days={shown?.preferredDays}
        daysZoneLabel={daysZoneLabel}
        firstDayOfWeek={firstDayOfWeek}
        note={pendingNote}
        onChangeDays={onChangePreferredDays}
        pending={!!pendingRequest}
      />

      {(pendingRequest || !!recentDecisions.length) && (
        <SectionShell title="Availability Requests">
          <div className="flex flex-col">
            {pendingRequest && (
              <div className="border-border gap-sm py-xs flex items-center border-b last:border-b-0">
                <span className="text-color-strong flex-1 text-sm">
                  {pendingNote}
                </span>
                <ButtonWithActionModal
                  actionText="Withdraw"
                  body="Your availability stays as it is now."
                  onAction={() => onWithdrawAvailabilityChange(pendingRequest)}
                  size="xs"
                  subtle
                  title="Withdraw this change?"
                  variant="critical"
                >
                  Withdraw
                </ButtonWithActionModal>
              </div>
            )}
            {recentDecisions.map((change) => {
              // shortcut: the backend never writes history.updatedAt, so
              // only the submission date is known; show the decision date
              // once the backend records one (handoff 2026-10-23).
              const submittedAt = change.history?.createdAt

              return (
                <div
                  key={change.id}
                  className="border-border gap-xxs py-xs flex flex-col border-b last:border-b-0"
                >
                  <div className="gap-xs flex items-center text-sm">
                    <Tag
                      value={DECISION_TAGS[change.status].label}
                      variant={DECISION_TAGS[change.status].variant}
                    />
                    {submittedAt && (
                      <span className="text-color-subtle">
                        Submitted {dayjs(submittedAt).format('MMM D, YYYY')}
                      </span>
                    )}
                  </div>
                  {change.status === 'declined' && change.declineReason && (
                    <span className="text-color-critical text-xs">
                      {change.declineReason}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </SectionShell>
      )}
    </div>
  )
}
